"use client";

import {
  FormCommandSelectField,
  FormInputField,
  FormMultiSelect,
  FormSelectField,
  FormTextarea,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { CdvType } from "@enums";
import { useHubCdv } from "@query";
import { useMemo } from "react";
import type { Control } from "react-hook-form";

export interface IDetailsFormValues {
  name: string;
  label: string;
  description: string;
  parentCatalogTypes: string[];
  installUrl: string;
}

interface ICatalogTypeDetailsFormProps {
  isLoading: boolean;
  control: Control<IDetailsFormValues>;
  parentCatalogTypeOptions: {
    value: string;
    label: string;
    description?: string;
  }[];
}

export function CatalogTypeDetailsForm({
  isLoading,
  control,
  parentCatalogTypeOptions,
}: ICatalogTypeDetailsFormProps) {
  const { t } = useTranslation();
  const { data: cdvs, isLoading: isLoadingCdvs } = useHubCdv();
  const isLoadingFields = useMemo(
    () => isLoading || isLoadingCdvs,
    [isLoading, isLoadingCdvs],
  );

  const installUrlOptions = useMemo(() => {
    if (!cdvs) return [];
    return cdvs
      .filter((cdv) => cdv.type === CdvType.InstallURL)
      .map((cdv) => ({
        value: cdv.name,
        label: cdv.label,
      }));
  }, [cdvs]);

  const catalogTypeNameOptions = useMemo(() => {
    if (!cdvs) return [];
    return cdvs
      .filter((cdv) => cdv.type === CdvType.CatalogTypeName)
      .map((cdv) => ({
        value: cdv.name,
        label: cdv.label || cdv.name,
      }));
  }, [cdvs]);

  return (
    <div className="mx-auto grid max-w-2xl gap-4 md:grid-cols-2 pl-1">
      <FormInputField
        isLoading={isLoadingFields}
        control={control}
        name="label"
        label={t("hub.catalog_type_manager.label")}
        placeholder={t("hub.catalog_type_manager.label_placeholder")}
        description={t("hub.catalog_type_manager.label_description")}
        required
      />
      <FormSelectField
        isLoading={isLoadingFields}
        control={control}
        name="name"
        label={t("hub.create_catalog.name")}
        placeholder={t("hub.catalog_type_manager.system_name_placeholder")}
        description={t("hub.catalog_type_manager.system_name_description")}
        items={catalogTypeNameOptions}
        enableSearch
        validate={(value) => {
          if (value && typeof value === "string") {
            if (/\s/.test(value)) {
              return t("hub.catalog_type_manager.system_name_no_spaces");
            }
            if (value !== value.toLowerCase()) {
              return t("hub.catalog_type_manager.system_name_lowercase", {
                defaultValue: "Name must be lowercase",
              });
            }
          }
          return true;
        }}
        required
      />

      <FormMultiSelect
        isLoading={isLoadingFields}
        control={control}
        name="parentCatalogTypes"
        label={t("hub.catalog_type_manager.parent_catalog_types")}
        items={parentCatalogTypeOptions}
        placeholder={t("hub.create_catalog.catalog_type_placeholder")}
        description={t(
          "hub.catalog_type_manager.parent_catalog_types_description",
        )}
      />
      <FormCommandSelectField
        control={control}
        name="installUrl"
        label={t("hub.catalog_type_manager.install_url")}
        placeholder={t("hub.catalog_type_manager.install_url_placeholder")}
        description={t("hub.catalog_type_manager.install_url_description")}
        options={installUrlOptions}
        isLoading={isLoadingFields}
        allowFreeText={true}
      />
      <div className="md:col-span-2">
        <FormTextarea
          isLoading={isLoadingFields}
          control={control}
          name="description"
          label={t("hub.catalog_subscribe.solution_description")}
          placeholder={t("hub.catalog_type_manager.description_placeholder")}
          description={t("hub.catalog_type_manager.description_placeholder")}
          rows={3}
          maxLength={250}
        />
      </div>
    </div>
  );
}
